import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import {
  PageHero,
  ArrowLeft,
  Typography,
  RadioGroup,
  RadioGroupItem,
  InputField,
  Dropdown,
  DropdownOption,
  Button,
} from '@dreamteam-monorepo/ui';
import { useLeaguesStore, fetchActiveTournamentCalendar } from '@/stores/LeaguesStore';
import { useAuthStore } from '@/stores/AuthStore';
import { ContentAdSlot } from '@/ads';
import { showGhLogin } from '@/lib/ghAuth';
import { fetchCurrentMatchday, fetchLeaderboardPeriods, getErrorMessage } from '@/lib/network';
import { usePeriodLabels } from '@/hooks/usePeriodLabels';
import type { PeriodLabels, PeriodAxis } from '@/lib/periodLabels';
import type { BECurrentMatchdayWindow, LeaderboardPeriod } from '@/lib/network';
import { trackFantasyEvent } from '@/analytics/fantasyEventTracking';

/** World Cup stage names, appended to the period label. No equivalent on a league season. */
const MATCHDAY_STAGE_NAMES: Record<number, string> = {
  1: 'Group Stage 1',
  2: 'Group Stage 2',
  3: 'Group Stage 3',
  4: 'Round of 32',
  5: 'Round of 16',
  6: 'Quarter-Finals',
  7: 'Semi-Finals',
  8: 'Third-place playoff and Final',
};

const buildScoringStartOptions = (
  periods: LeaderboardPeriod[],
  labels: PeriodLabels
): DropdownOption[] =>
  periods.map(period => {
    const value = String(period.number);
    const stage = labels.isGameweek ? undefined : MATCHDAY_STAGE_NAMES[period.number];

    return {
      id: value,
      label: stage ? `${labels.long} ${value} – ${stage}` : `${labels.long} ${value}`,
      value,
    };
  });

/**
 * Statuses a league can still start scoring in — the period's deadline has not
 * passed. A gameweek is `open` from its predecessor's finalisation until its own
 * deadline; the matchday axis has no equivalent and goes straight to `active`.
 */
const NOT_STARTED_STATUSES: Record<PeriodAxis, string[]> = {
  gameweek: ['upcoming', 'open'],
  matchday: ['upcoming'],
};

/** The period a new league should score from, before it is matched to the real list. */
const getTargetPeriodNumber = (
  window: BECurrentMatchdayWindow,
  axis: PeriodAxis
): number | null => {
  const current = axis === 'gameweek' ? window.currentGameweek : window.currentMatchday;
  const next = axis === 'gameweek' ? window.nextGameweek : window.nextMatchday;

  if (!current) {
    return null;
  }
  if (NOT_STARTED_STATUSES[axis].includes(current.status)) {
    return current.number;
  }
  return next?.number ?? current.number + 1;
};

/**
 * Falls forward to the first period at or after the target, so a target the
 * tournament has already passed — or a season that has ended — still resolves to
 * a real option rather than leaving the field blank.
 */
const resolveScoringStartValue = (
  periods: LeaderboardPeriod[],
  target: number | null
): string[] => {
  if (periods.length === 0) {
    return [];
  }

  const period =
    (target === null ? undefined : periods.find(p => p.number >= target)) ??
    periods[periods.length - 1];

  return period ? [String(period.number)] : [];
};

const CreateLeague: FC = () => {
  const navigate = useNavigate();
  const userId = useAuthStore(s => s.userId);
  const { createLeague, creating } = useLeaguesStore();
  const periodLabels = usePeriodLabels();

  const [periods, setPeriods] = useState<LeaderboardPeriod[]>([]);
  const [privacy, setPrivacy] = useState<'private' | 'public'>('private');
  const [name, setName] = useState('');
  const [scoringStartsValue, setScoringStartsValue] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [tournamentCalendarId, setTournamentCalendarId] = useState<string | null>(null);
  const [currentPeriodLoadFailed, setCurrentPeriodLoadFailed] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const loadDefaults = async () => {
      try {
        const tc = await fetchActiveTournamentCalendar();
        if (!tc || isCancelled) {
          return;
        }

        setTournamentCalendarId(tc.id);

        // The tournament's own period list, so the options run to however many
        // periods it has — 8 on the World Cup, a full season on the EPL.
        const periodsResponse = await fetchLeaderboardPeriods(tc.id, true);
        if (isCancelled) {
          return;
        }

        const { periods: tournamentPeriods, type: axis } = periodsResponse.data;
        setPeriods(tournamentPeriods);

        let target: number | null = null;
        try {
          const windowResponse = await fetchCurrentMatchday({ tournamentCalendarId: tc.id });
          if (isCancelled) {
            return;
          }
          target = getTargetPeriodNumber(windowResponse.data, axis);
        } catch {
          if (isCancelled) {
            return;
          }

          setScoringStartsValue([]);
          setCurrentPeriodLoadFailed(true);
          toast.error('Could not load the current scoring period. Please refresh and try again.');
          return;
        }

        const defaultScoringStartsValue = resolveScoringStartValue(tournamentPeriods, target);
        if (defaultScoringStartsValue.length > 0) {
          setScoringStartsValue(currentValue =>
            currentValue.length > 0 ? currentValue : defaultScoringStartsValue
          );
        }
      } catch {
        // Without a calendar or its period list there is nothing to pick from, so
        // the form stays disabled rather than offering an invalid scoring start.
      }
    };

    void loadDefaults();

    return () => {
      isCancelled = true;
    };
  }, []);

  const canSubmit =
    !currentPeriodLoadFailed &&
    name.trim().length > 0 &&
    scoringStartsValue.length > 0 &&
    !!tournamentCalendarId &&
    !!userId;

  const handleSubmit = async () => {
    if (!userId) {
      toast.error('You must be logged in to create a league');
      showGhLogin();
      return;
    }
    if (!canSubmit || !tournamentCalendarId) return;

    try {
      const leagueId = await createLeague({
        name: name.trim(),
        description: description.trim() || undefined,
        is_public: privacy === 'public',
        start_gameweek: Number(scoringStartsValue[0]),
        tournament_calendar_id: tournamentCalendarId,
      });
      trackFantasyEvent('leagueCreated');
      toast.success('League created successfully!');
      navigate(`/leagues/${leagueId}`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to create league'));
    }
  };

  const handleScoringStartsChange = (option: DropdownOption, checked: boolean) => {
    setScoringStartsValue(checked ? [option.value] : []);
  };

  return (
    <div className="components light flex flex-col">
      <PageHero>
        <div className="flex flex-col gap-[--padding-md]">
          <button
            type="button"
            onClick={() => {
              trackFantasyEvent('leagueLink', { link_name: 'Back to Leagues' });
              navigate('/leagues');
            }}
            className="flex items-center gap-2 self-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 rounded-[--corner-xs]"
          >
            <ArrowLeft className="size-6 text-[--color-blue-200]" />
            <Typography as="span" variant="button020" className="text-[--color-blue-200]">
              Leagues
            </Typography>
          </button>

          <Typography
            as="h1"
            variant="headline050"
            className="text-[--page-header-header] !font-[900] sm:text-40 sm:leading-44"
          >
            CREATE A LEAGUE
          </Typography>
        </div>
      </PageHero>

      <div className="max-w-[846px] mx-auto w-full px-[--padding-md] sm:px-0 py-[--padding-3xl]">
        <div className="flex flex-col gap-[--padding-lg] max-w-[500px]">
          <RadioGroup
            value={privacy}
            onValueChange={v => setPrivacy(v as 'private' | 'public')}
            className="flex-row gap-[--padding-xl]"
          >
            <RadioGroupItem value="private" id="privacy-private" label="Private" />
            <RadioGroupItem value="public" id="privacy-public" label="Public" />
          </RadioGroup>

          <InputField
            id="league-name"
            label="League Name*"
            placeholder="Maximum 30 characters"
            value={name}
            onChange={e => setName(e.target.value.slice(0, 30))}
            maxCharCount={30}
            supportText="Inappropiate names will not be accepted."
            onWhite
          />

          <Dropdown
            label="Scoring Starts*"
            options={buildScoringStartOptions(periods, periodLabels)}
            values={scoringStartsValue}
            emptyValue={`Select ${periodLabels.long}`}
            onValueChange={handleScoringStartsChange}
            completed={scoringStartsValue.length > 0}
            disabled={currentPeriodLoadFailed}
            onWhite
          />
          <Typography
            variant="smallprint010R"
            className="text-[--inputfield-label-color] -mt-[--padding-xs]"
          >
            This will be when scoring starts for your league.
          </Typography>

          <fieldset className="flex flex-col gap-[--padding-xs]">
            <Typography
              as="label"
              htmlFor="league-description"
              variant="paragraph010"
              className="text-[--inputfield-label-color]"
            >
              League Description
            </Typography>
            <div className="bg-[--inputfield-input-bg-default] border border-transparent hover:border-[--inputfield-input-border-active] focus-within:border-[--inputfield-input-border-active] rounded-[--corner-xs] p-[--padding-md]">
              <textarea
                id="league-description"
                rows={4}
                placeholder="Who is the league for? Any additional rules..."
                value={description}
                onChange={e => setDescription(e.target.value.slice(0, 100))}
                className="w-full bg-transparent outline-none resize-none font-primary text-14 leading-20 text-[--color-neutral-900] placeholder:text-[--color-neutral-600]"
              />
            </div>
            <div className="flex justify-between">
              <Typography variant="smallprint010R" className="text-[--inputfield-label-color]">
                Optional
              </Typography>
              <Typography variant="smallprint010R" className="text-[--inputfield-label-color]">
                {description.length}/100
              </Typography>
            </div>
          </fieldset>

          <Button
            variant="contained"
            color="blue"
            disabled={!canSubmit || creating}
            loading={creating}
            onClick={handleSubmit}
            className="w-full sm:w-auto"
          >
            Create League
          </Button>
        </div>
      </div>
      <ContentAdSlot id="ad-boa-leaderboard" />
    </div>
  );
};

export default CreateLeague;
